// Store Imports
import { computed } from '@angular/core'
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'
// Shared Imports
import { ELocalStorageKeys, ERoles, ECountry, ECompany } from '@shared/models'
// Thirdparty Imports
import { jwtDecode } from 'jwt-decode'

export interface AuthState {
	logged: boolean
	accessToken: string | null
	refreshToken: string | null
	roles: ERoles[]
	country: ECountry
	companies: ECompany[]
	lastUserEmail: string | null
}

const initialState: AuthState = {
	logged: false,
	accessToken: '',
	refreshToken: '',
	roles: [ERoles.viewer],
	country: ECountry.mexico,
	companies: [],
	lastUserEmail: null,
}

export const AuthStore = signalStore(
	{ providedIn: 'root' },
	withState(() => {
		const accessToken = window.localStorage.getItem(ELocalStorageKeys.accessToken) ?? ''
		const refreshToken = window.localStorage.getItem(ELocalStorageKeys.refreshToken) ?? ''

		// With a stored session, the remembered email is that session's (even if it has none)
		const lastUserEmail = accessToken
			? rememberUserEmail(accessToken)
			: window.localStorage.getItem(ELocalStorageKeys.lastUserEmail)

		// Don't mutate initialState: signout() resets to it, and a mutated copy kept the old token in memory
		return accessToken
			? { ...initialState, accessToken, refreshToken, lastUserEmail }
			: { ...initialState, lastUserEmail }
	}),
	withComputed(({ accessToken, lastUserEmail }) => ({
		logged: computed(() => Boolean(accessToken()?.length)),
		companies: computed(() => {
			// console.log('time', accessToken())

			const { companies } = decodeJwt(accessToken())
			return companies
		}),
		/**
		 * Claim `name` del JWT logueado — mismo claim que lee `getUserIdentityFromToken()`
		 * en backend para `shift_preload.created_by` (`oms-api`, PR !24573). Se usa para
		 * pre-seleccionar el "Asistente" de Pre-Checkin (`StepHubUnitComponent`) por
		 * coincidencia de nombre con el catálogo de conductores tipo `manager` — no hay
		 * vínculo por id/email hoy (`IDriver` no expone email), es un best-effort editable.
		 * Si el JWT no trae el claim `name` (no debería pasar en producción, todo login
		 * pasa por Azure AD), `decodeJwt(...).name` resuelve a `undefined` — el consumidor
		 * (`StepHubUnitComponent`) ya trata la ausencia de match como "sin pre-selección",
		 * no como error.
		 */
		currentUserName: computed(() => decodeJwt(accessToken()).name),
		/** `email` claim of the current session, or null when signed out. */
		currentUserEmail: computed(() => decodeEmail(accessToken())),
		/**
		 * Signed in: the current session's email (null if its token has none — never a previous
		 * user's). Signed out: the last session's email (e.g. after a failed token refresh).
		 */
		knownUserEmail: computed(() => (accessToken() ? decodeEmail(accessToken()) : lastUserEmail())),
	})),
	withMethods((store) => ({
		signin: ({ accessToken, refreshToken }: AccessAndRefreshToken): void => {
			// console.log('signin')
			// We save the tokens in the local storage
			window.localStorage.setItem(ELocalStorageKeys.accessToken, accessToken)
			window.localStorage.setItem(ELocalStorageKeys.refreshToken, refreshToken)

			const lastUserEmail = rememberUserEmail(accessToken)
			patchState(store, (_state: Partial<AuthState>) => ({
				accessToken,
				refreshToken,
				lastUserEmail,
			}))
		},
		/**
		 * `forgetUser: true` for an explicit "Cerrar Sesión": also drops the remembered email.
		 * Forced signouts (failed refresh, guard) keep it so error captures still show who it was.
		 */
		signout: ({ forgetUser = false }: { forgetUser?: boolean } = {}): void => {
			// We remove the tokens from the local storage
			window.localStorage.removeItem(ELocalStorageKeys.accessToken)
			window.localStorage.removeItem(ELocalStorageKeys.refreshToken)
			if (forgetUser) {
				window.localStorage.removeItem(ELocalStorageKeys.lastUserEmail)
			}

			patchState(store, (_state: Partial<AuthState>) => ({
				...initialState,
				lastUserEmail: forgetUser ? null : store.lastUserEmail(),
			}))
		},
		updateAccessAndRefreshToken: ({ accessToken, refreshToken }: AccessAndRefreshToken): void => {
			// We save the tokens in the local storage
			window.localStorage.setItem(ELocalStorageKeys.accessToken, accessToken)
			window.localStorage.setItem(ELocalStorageKeys.refreshToken, refreshToken)

			const lastUserEmail = rememberUserEmail(accessToken)
			patchState(store, (_state: Partial<AuthState>) => ({
				accessToken,
				refreshToken,
				lastUserEmail,
			}))
		},
		updateCountry: ({ country }: { country: ECountry }): void => {
			patchState(store, (_state: Partial<AuthState>) => ({ country }))
		},
	}))
)

/** Never throws: a malformed token decodes as "no claims" (no companies, name or email). */
function decodeJwt(accessToken: string | null): JwtDecoded {
	// console.log('accessToken', accessToken)
	const companies: ECompany[] = []
	let name: string | null = null
	let email: string | null = null

	const decode = safeJwtDecode(accessToken)
	if (decode) {
		const { company } = decode
		name = decode.name ?? null
		email = decode.email || null

		switch (company) {
			case 1:
				companies.push(ECompany.zee)
				break
			default:
				break
		}
		// For the case where we re received the value of company in the jet (ZEE)
		company || companies.push(ECompany.zee)
	}

	const jwtDecoded: JwtDecoded = { companies, name, email }
	// console.log('decoded', decoded)
	// console.log('jwtDecoded', jwtDecoded)
	return jwtDecoded
}

interface AccessAndRefreshToken {
	accessToken: string
	refreshToken: string
}

function safeJwtDecode(
	accessToken: string | null
): { company?: number; name?: string; email?: string } | null {
	if (!accessToken) return null
	try {
		return jwtDecode<{ company?: number; name?: string; email?: string }>(accessToken)
	} catch {
		return null
	}
}

/** `email` claim of the token; null when missing or the token is malformed. */
function decodeEmail(accessToken: string | null): string | null {
	return decodeJwt(accessToken).email
}

/**
 * Makes the token's email the remembered one and returns it. A token without email clears it,
 * so a previous user's email is never attributed to the current session.
 */
function rememberUserEmail(accessToken: string | null): string | null {
	const email = decodeEmail(accessToken)
	if (email) {
		window.localStorage.setItem(ELocalStorageKeys.lastUserEmail, email)
	} else {
		window.localStorage.removeItem(ELocalStorageKeys.lastUserEmail)
	}
	return email
}

interface JwtDecoded {
	companies: ECompany[]
	name: string | null
	email: string | null
}
